import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';

function AdminLogo() {
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const load = () => api.get('/logo').then(r => {
    setLogo(r.data);
    setPreview(null);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('logo', file);
    try {
      await api.post('/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch {
      alert('Error al subir el logo');
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar el logo actual?')) return;
    await api.delete('/logo');
    load();
  };

  const currentSrc = preview || (logo?.exists ? logo.url + '?t=' + Date.now() : null);

  return (
    <>
      <div className="admin-topbar"><h1>Logo del Colegio</h1></div>
      <div className="admin-content">
        <div className="admin-card">
          <h3>Logo actual</h3>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Este logo aparece en la barra de navegación y en la sección principal (hero) de la página.
            Recomendamos una imagen cuadrada o con fondo transparente (PNG/SVG) de al menos 200x200px.
          </p>

          <div style={{
            display: 'flex',
            gap: '2rem',
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Preview */}
            <div style={{
              width: 200,
              height: 200,
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-alt)',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              {currentSrc ? (
                <img src={currentSrc} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 16 }} />
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-lighter)' }}>
                  <span className="material-icons" style={{ fontSize: '3rem', display: 'block', marginBottom: 8 }}>image</span>
                  <span style={{ fontSize: '0.85rem' }}>Sin logo</span>
                </div>
              )}
            </div>

            {/* Previews in context */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: '0.8rem' }}>
                Vista previa en la página
              </p>

              {/* Navbar preview */}
              <div style={{
                background: 'var(--primary-dark)',
                padding: '12px 20px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: '1rem'
              }}>
                {currentSrc ? (
                  <img src={currentSrc} alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8 }} />
                ) : (
                  <div style={{
                    width: 36, height: 36, background: 'linear-gradient(135deg, #d4a843, #f0d78c)',
                    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--primary-dark)', fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '1rem'
                  }}>L</div>
                )}
                <span style={{ color: 'white', fontFamily: 'var(--font-heading)', fontSize: '0.9rem', fontWeight: 600 }}>
                  Liceo Adolfo León Gómez
                </span>
              </div>

              {/* Hero preview */}
              <div style={{
                background: 'linear-gradient(135deg, var(--primary-dark), var(--primary))',
                padding: '24px',
                borderRadius: 'var(--radius-sm)',
                textAlign: 'center'
              }}>
                {currentSrc && (
                  <img src={currentSrc} alt="Logo" style={{
                    width: 70, height: 70, objectFit: 'contain', margin: '0 auto 8px',
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                  }} />
                )}
                <div style={{ color: 'white', fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>
                  Liceo Adolfo León Gómez
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginTop: 4 }}>
                  Formando líderes con valores
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h3>Cambiar logo</h3>
          <div className="upload-zone" onClick={() => fileRef.current?.click()}>
            <span className="material-icons">cloud_upload</span>
            <p>Haz clic para seleccionar una imagen</p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-lighter)' }}>PNG, JPG, WEBP o SVG (máx. 5MB)</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.8rem' }}>
            <button className="btn-save" onClick={handleUpload} disabled={uploading || !preview}>
              <span className="material-icons" style={{ fontSize: '1rem' }}>upload</span>
              {uploading ? 'Subiendo...' : 'Guardar logo'}
            </button>
            {logo?.exists && (
              <button className="btn-danger" onClick={handleDelete}>
                <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }}>delete</span>
                Eliminar logo
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminLogo;
